import type { StatusType } from "@datadog/browser-logs";
import { datadogLogs } from "@datadog/browser-logs";
import type { ErrorType } from "./interfaces";

interface DatadogLoggerConfig {
	/** Browser-safe client token from Datadog. */
	clientToken: string;
	/** Datadog site. Defaults to 'datadoghq.com'. */
	site?: string;
	/** Service name shown in Datadog Log Explorer. */
	service?: string;
}

const VALID_LEVELS: StatusType[] = ["debug", "info", "warn", "error"];

// Maps a log level threshold to the console methods that should be forwarded.
// console.log is treated as info-level since Datadog maps it to "info".
const CONSOLE_METHODS_BY_LEVEL: Record<
	string,
	("log" | "debug" | "info" | "warn" | "error")[]
> = {
	debug: ["debug", "log", "info", "warn", "error"],
	info: ["log", "info", "warn", "error"],
	warn: ["warn", "error"],
	error: ["error"],
};

function resolveLogLevel(): StatusType {
	const raw = process.env.REACT_APP_DATADOG_LOG_LEVEL?.toLowerCase();
	if (raw && (VALID_LEVELS as string[]).includes(raw)) {
		return raw as StatusType;
	}
	return "error";
}

class DatadogLogger {
	private initialized = false;

	init(config: DatadogLoggerConfig): void {
		if (this.initialized) return;

		const { clientToken, site = "datadoghq.com", service } = config;

		if (!clientToken) {
			console.warn(
				"[DatadogLogger] No client token provided — logging disabled.",
			);
			return;
		}

		const level = resolveLogLevel();

		datadogLogs.init({
			clientToken,
			site,
			service,
			env: (process.env.REACT_APP_SLOT ?? "unknown").toLowerCase(),
			version: (process.env.REACT_APP_VERSION ?? "unknown").toLowerCase(),
			// Captures uncaught JS errors, unhandled promise rejections, network errors,
			// and console.error — these are bundled together by the SDK and cannot be separated.
			forwardErrorsToLogs: true,
			// Forward console methods at or above the configured log level.
			// console.error is always included via forwardErrorsToLogs regardless.
			forwardConsoleLogs: CONSOLE_METHODS_BY_LEVEL[level],
			sessionSampleRate: 100,
		});

		datadogLogs.logger.setLevel(level);

		this.initialized = true;
	}

	// ---------------------------------------------------------------------------
	// Public logging methods
	// ---------------------------------------------------------------------------

	logApiCall(data: {
		endpoint: string;
		method: string;
		status: number;
		duration_ms: number;
		success: boolean;
		error_type?: ErrorType;
	}): void {
		if (!this.initialized) return;

		const message = `[api_call] ${data.method} ${data.endpoint} → ${data.status}`;
		if (data.success) {
			datadogLogs.logger.info(message, { type: "api_call", ...data });
		} else {
			datadogLogs.logger.error(message, { type: "api_call", ...data });
		}
	}

	logException(data: {
		error_message: string;
		stack?: string;
		source?: string;
		line?: number;
		col?: number;
	}): void {
		if (!this.initialized) return;

		datadogLogs.logger.error(`[exception] ${data.error_message}`, {
			type: "exception",
			...data,
		});
	}

	logConsole(level: "error" | "warn", args: unknown[]): void {
		if (!this.initialized) return;

		const argsStr = args
			.map((a) => (typeof a === "object" ? JSON.stringify(a) : String(a)))
			.join(" ");
		const type = level === "error" ? "console_error" : "console_warn";
		const message = `[${type}] ${argsStr}`;
		if (level === "error") {
			datadogLogs.logger.error(message, { type, args: argsStr });
		} else {
			datadogLogs.logger.warn(message, { type, args: argsStr });
		}
	}
}

// Singleton — import and call `.init()` once at app startup.
export default new DatadogLogger();
